import { ContentCard } from "@msuite/ui/content-card";
import { FileUpload, useFileUpload, useRegisterFileUploadController } from "@msuite/ui/file-upload";
import { FileInspectorButton } from "@msuite/ui/files";
import type { Disclosure } from "@msuite/ui/hooks";
import { icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { Portal } from "@msuite/ui/portal";
import { responseHandler } from "@msuite/ui/utils";
import type { Nullable } from "@msuite/utils/types";
import React from "react";
import { useUpload } from "@/api/files";
import { FileCardRemote } from "@/components/files";
import Notes, { NoteHintButton } from "@/components/notes";
import { client } from "@/hono";
import { useCalculationEditorContext } from "@/sites/calculations/calculation-editor/context";

/** Props Interface */
interface CalculationFormItemFilesAndNotesProps {
	serviceCalculationItemId: Nullable<string>;
	notesDisclosure: Disclosure;
	filesDisclosure: Disclosure;
	containerRef: React.RefObject<HTMLDivElement | null>;
}

export function CalculationFormItemFilesAndNotes({
	serviceCalculationItemId,
	notesDisclosure,
	filesDisclosure,
	containerRef,
}: CalculationFormItemFilesAndNotesProps) {
	/** Hooks */
	const { calculationId } = useCalculationEditorContext();
	const { uploadFn } = useUpload();
	const { t } = useTranslation();
	const fileUploadController = useFileUpload({
		uploadFn,
		defaultFiles: async () => {
			if (!serviceCalculationItemId) return [];
			const response = await client.files[":tableName"][":key"][":identifier"].$get({
				param: {
					tableName: "serviceCalculationItemFiles",
					key: "serviceCalculationItemId",
					identifier: serviceCalculationItemId,
				},
			});
			const data = await responseHandler(response);
			return data.files;
		},
		onComplete: async (uploadResults) => {
			if (!serviceCalculationItemId) return;
			await client.calculations[":calculationId"].services[":serviceCalculationItemId"].files.$post(
				{
					param: {
						calculationId,
						serviceCalculationItemId,
					},
					json: { uploadResults },
				},
			);
		},
	});

	useRegisterFileUploadController(serviceCalculationItemId || "BLANK", fileUploadController);

	/** Guard */
	if (!serviceCalculationItemId) return null;

	/** Render */
	return (
		<React.Fragment>
			<div className="h-stack justify-end">
				<FileInspectorButton
					onClick={filesDisclosure.onToggle}
					itemsLength={fileUploadController.length}
					variant="white"
					isLoading={
						fileUploadController.isUploading || fileUploadController.isFetchingDefaultFiles
					}
				/>
				<NoteHintButton
					notesTable="serviceCalculationItemNotes"
					identifier={serviceCalculationItemId}
					disclosure={notesDisclosure}
					variant="white"
				/>
			</div>
			{notesDisclosure.isOpen && (
				<Portal containerRef={containerRef}>
					<div className="mt-2">
						<Notes
							notesTable="serviceCalculationItemNotes"
							identifier={serviceCalculationItemId}
						/>
					</div>
				</Portal>
			)}
			{filesDisclosure.isOpen && (
				<Portal containerRef={containerRef}>
					<ContentCard
						icon={icon("File")}
						title={t("files.files")}
						variant="plain"
						className="mt-2"
					>
						<FileUpload
							withLabel={false}
							controller={fileUploadController}
							renderFile={(file) => (
								<FileCardRemote
									fileId={file.id}
									onInteraction={async () => {}}
								/>
							)}
						/>
					</ContentCard>
				</Portal>
			)}
		</React.Fragment>
	);
}
